import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SVGIcon from '../SVGIcon/SVGIcon';
import { SearchService } from '../../services/SearchService';
import { API_BASE_URL } from '../../config';
import './Header.css';

export default function Header({
  activePage = 'home',
  cartItems = [],
  wishlistItems = [],
  onOpenCart = () => { },
  onOpenWishlist = () => { },
  onOpenAuth = () => { },
  triggerNotification = () => { },
  selectedCurr = 'INR',
  setSelectedCurr = () => { },
  t = (key) => key,
  onNavigate,
  isProductDetailsOpen,
  onProductClick,
  onBlogClick,
  currentUser = null,
  onLogout = () => { },
  onOpenAdmin = () => { },
  onOpenOrderHistory = () => { },
  onOpenProfile = () => { }
}) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeCurrMenu, setActiveCurrMenu] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrolledMenuOpen, setScrolledMenuOpen] = useState(false);

  // Bulk Order Modal States
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkFormData, setBulkFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    product: 'BIO-ORBI Super Novel',
    quantity: '',
    location: '',
    message: ''
  });
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSuccess, setBulkSuccess] = useState(false);
  const [bulkError, setBulkError] = useState('');

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    setBulkLoading(true);
    setBulkError('');
    setBulkSuccess(false);

    try {
      const response = await fetch(`${API_BASE_URL}/orders/bulk-enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bulkFormData)
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setBulkSuccess(true);
        triggerNotification('Bulk order enquiry submitted successfully!');
        setBulkFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          product: 'BIO-ORBI Super Novel',
          quantity: '',
          location: '',
          message: ''
        });
      } else {
        setBulkError(data.message || 'Failed to submit enquiry.');
      }
    } catch (err) {
      setBulkError('Network error. Failed to connect to server.');
    } finally {
      setBulkLoading(false);
    }
  };

  // Autocomplete / Search Suggestions States
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);
  const [selectedSearchCategory, setSelectedSearchCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState({ products: [], blogs: [], categories: [], pages: [] });

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults({ products: [], blogs: [], categories: [], pages: [] });
      setLoading(false);
      return;
    }

    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      SearchService.search(searchQuery, selectedSearchCategory).then((results) => {
        setSearchResults(results);
        setLoading(false);
      });
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, selectedSearchCategory]);

  const handleProductSuggestionClick = (prod) => {
    setSearchQuery('');
    setSuggestionsVisible(false);
    if (onProductClick) {
      onProductClick(prod);
    }
  };

  const handleBlogSuggestionClick = (blog) => {
    setSearchQuery('');
    setSuggestionsVisible(false);
    if (onBlogClick) {
      onBlogClick(blog);
    }
  };

  const handleCategorySuggestionClick = (cat) => {
    setSearchQuery('');
    setSuggestionsVisible(false);
    setSelectedSearchCategory(cat.slug);
    triggerNotification(`Category filter set to: ${cat.name}`);
    if (onNavigate) {
      onNavigate('#products');
    }
  };

  const handlePageSuggestionClick = (page) => {
    setSearchQuery('');
    setSuggestionsVisible(false);
    if (onNavigate) {
      onNavigate(page.hash);
    }
  };

  const renderSuggestionsDropdown = (isMobile) => {
    const totalResults = searchResults.products.length +
      searchResults.blogs.length +
      searchResults.categories.length +
      searchResults.pages.length;

    return (
      <div className={`search-suggestions-dropdown ${isMobile ? 'mobile-dropdown' : ''}`}>
        {loading ? (
          <div className="search-loading-state">
            <div className="search-spinner"></div>
            <span>Searching database...</span>
          </div>
        ) : totalResults === 0 ? (
          <div className="no-suggestions">
            No results found matching "{searchQuery}"
          </div>
        ) : (
          <div className="suggestions-grouped-container">
            {/* Products Group */}
            {searchResults.products.length > 0 && (
              <div className="suggestions-group">
                <div className="suggestions-group-header">Products</div>
                <div className="suggestions-group-items">
                  {searchResults.products.map((prod) => (
                    <div
                      key={`prod-${prod.id}`}
                      className="suggestion-item product-suggestion"
                      onClick={() => handleProductSuggestionClick(prod)}
                    >
                      <div className="suggestion-img-wrap">
                        <img
                          src={prod.image}
                          alt={prod.title}
                          onError={e => { e.target.src = '/products/Super Novel.png'; }}
                        />
                      </div>
                      <div className="suggestion-info">
                        <span className="suggestion-title">{prod.title}</span>
                        <span className="suggestion-meta">
                          {prod.category} &bull; {convertPrice(prod.price, selectedCurr)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Blogs Group */}
            {searchResults.blogs.length > 0 && (
              <div className="suggestions-group">
                <div className="suggestions-group-header">Articles & Insights</div>
                <div className="suggestions-group-items">
                  {searchResults.blogs.map((blog) => (
                    <div
                      key={`blog-${blog.id}`}
                      className="suggestion-item blog-suggestion"
                      onClick={() => handleBlogSuggestionClick(blog)}
                    >
                      <div className="suggestion-blog-badge">
                        <span className="day">{blog.date}</span>
                        <span className="month">{blog.month}</span>
                      </div>
                      <div className="suggestion-info">
                        <span className="suggestion-title">{blog.title}</span>
                        <span className="suggestion-meta">🏷️ {blog.category} &bull; Blog</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Categories Group */}
            {searchResults.categories.length > 0 && (
              <div className="suggestions-group">
                <div className="suggestions-group-header">Categories</div>
                <div className="suggestions-group-items category-chips-row">
                  {searchResults.categories.map((cat) => (
                    <button
                      key={`cat-${cat.id}`}
                      type="button"
                      className="suggestion-category-chip"
                      onClick={() => handleCategorySuggestionClick(cat)}
                    >
                      🌱 {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Pages Group */}
            {searchResults.pages.length > 0 && (
              <div className="suggestions-group">
                <div className="suggestions-group-header">Quick Links</div>
                <div className="suggestions-group-items">
                  {searchResults.pages.map((page) => (
                    <div
                      key={`page-${page.id}`}
                      className="suggestion-item page-suggestion"
                      onClick={() => handlePageSuggestionClick(page)}
                    >
                      <div className="suggestion-page-icon">🔗</div>
                      <div className="suggestion-info">
                        <span className="suggestion-title">{page.name}</span>
                        <span className="suggestion-meta">{page.description}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleNavLinkClick = (e, hash) => {
    setMobileMenuOpen(false);
    setScrolledMenuOpen(false);
    if (onNavigate) {
      e.preventDefault();
      onNavigate(hash);
    }
  };

  const cartCount = cartItems.reduce((sum, item) => sum + item.qty, 0);
  const wishlistCount = wishlistItems.length;
  const cartTotal = cartItems.reduce((sum, item) => sum + (item.price * item.qty), 0);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
        setScrolledMenuOpen(false); // Close menu if we scroll back to top
      }
    };

    const handleClickOutside = (event) => {
      if (!event.target.closest('.utility-dropdown')) {
        setActiveCurrMenu(false);
      }
      if (!event.target.closest('.search-bar-wrap') && !event.target.closest('.drawer-search-mobile')) {
        setSuggestionsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('click', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const convertPrice = (val, curr) => {
    const rates = {
      INR: { rate: 1, symbol: '₹' },
      USD: { rate: 0.012, symbol: '$' },
      EUR: { rate: 0.011, symbol: '€' },
      GBP: { rate: 0.0094, symbol: '£' },
      AED: { rate: 0.044, symbol: 'د.إ ' },
      SGD: { rate: 0.016, symbol: 'S$' }
    };
    const config = rates[curr] || rates.INR;
    return `${config.symbol}${(val * config.rate).toFixed(2)}`;
  };



  const currencies = [
    { code: 'INR', label: '₹ INR — Indian Rupee' },
    { code: 'USD', label: '$ USD — US Dollar' },
    { code: 'EUR', label: '€ EUR — Euro' },
    { code: 'GBP', label: '£ GBP — British Pound' },
    { code: 'AED', label: 'د.إ AED — UAE Dirham' },
    { code: 'SGD', label: 'S$ SGD — Singapore Dollar' },
  ];

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      triggerNotification('Please enter a search term.');
      return;
    }

    const query = searchQuery.trim().toLowerCase();
    const hasProducts = searchResults.products && searchResults.products.length > 0;
    const hasBlogs = searchResults.blogs && searchResults.blogs.length > 0;
    const hasPages = searchResults.pages && searchResults.pages.length > 0;
    const hasCategories = searchResults.categories && searchResults.categories.length > 0;

    // 1. Page match first (direct page navigation)
    if (hasPages && (searchResults.pages[0].name.toLowerCase().includes(query) || ['about', 'contact', 'help', 'faq'].includes(query))) {
      handlePageSuggestionClick(searchResults.pages[0]);
      setMobileMenuOpen(false);
      return;
    }

    // 2. Product matches
    if (hasProducts) {
      // Find if there is an exact case-insensitive match for the query in product titles
      const exactProductMatch = searchResults.products.find(p => p.title.toLowerCase() === query);
      if (searchResults.products.length === 1 || exactProductMatch) {
        // Only one product matches OR exact title typed - open that product's detail page
        handleProductSuggestionClick(exactProductMatch || searchResults.products[0]);
      } else {
        // Multiple products match (e.g. typing "orbi" matches several) - show Shop page and filter
        setSearchQuery('');
        setSuggestionsVisible(false);
        navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      }
      setMobileMenuOpen(false);
      return;
    }

    // 3. Blog matches
    if (hasBlogs) {
      const exactBlogMatch = searchResults.blogs.find(b => b.title.toLowerCase() === query);
      if (searchResults.blogs.length === 1 || exactBlogMatch) {
        // Only one blog matches OR exact title typed - open that blog's detail page
        handleBlogSuggestionClick(exactBlogMatch || searchResults.blogs[0]);
      } else {
        // Multiple blogs match - show Knowledge Hub page and filter
        setSearchQuery('');
        setSuggestionsVisible(false);
        navigate(`/knowledge-hub?search=${encodeURIComponent(searchQuery.trim())}`);
      }
      setMobileMenuOpen(false);
      return;
    }

    // 4. Category matches
    if (hasCategories) {
      handleCategorySuggestionClick(searchResults.categories[0]);
      setMobileMenuOpen(false);
      return;
    }

    // 5. Default fallback: Search in shop page
    triggerNotification(`Searching for "${searchQuery}"...`);
    navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
    setMobileMenuOpen(false);
  };

  return (
    <div className="header-placeholder">
      <div className={`header-wrapper ${isScrolled ? 'scrolled' : ''}`}>
        {/* Top Banner Contact & Language Row (Desktop only) */}
        <div className="top-utility-bar">
          <div className="container utility-content">
            <div className="utility-left">
              <span onClick={() => setBulkModalOpen(true)} style={{ cursor: 'pointer', fontWeight: 600 }}>Bulk Order</span>
              <span className="divider-dot">|</span>
              <span className="flex-center">
                <SVGIcon name="phone" size={14} /> Support: +91 7228064418
              </span>
              <span className="divider-dot">|</span>
              <span className="flex-center">
                <SVGIcon name="mail" size={14} /> orbifertorganic@gmail.com
              </span>
            </div>
            <div className="utility-right">
            </div>
          </div>
        </div>

        {/* Main Header / Logo, Search & Cart */}
        <header className="main-header">
          <div className="container header-grid">
            {/* Mobile Hamburger button (hidden on desktop) */}
            <button className="hamburger-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle Menu">
              <SVGIcon name={mobileMenuOpen ? "close" : "menu"} size={26} />
            </button>

            {/* Logo + Brand Name */}
            <div className="logo-section" onClick={(e) => {
              if (onNavigate) {
                e.preventDefault();
                onNavigate('');
              } else {
                window.location.href = '/';
              }
            }}>
              <img src="/logo/Orbifert Logo.png" alt="ORBIFERT Logo" className="logo-full-img" />
              <div className="logo-brand-text h-logo-brand-text">
                <span className="brand-name-top h-brand-name-top">ORBIFERT</span>
                <span className="brand-name-sub h-brand-name-sub">ORGANIC PVT. LTD</span>
                <span className="brand-name-tagline h-brand-name-tagline">Reviving Soil, <span className="tagline-green h-tagline-green">Rewarding Farmers</span></span>
              </div>
            </div>

            {/* Navigation Links (Desktop only, merged with search bar row) */}
            <div className="nav-links-desktop-merged">
              <a href="#home" className={activePage === 'home' ? 'active' : ''} onClick={(e) => handleNavLinkClick(e, '#home')}>Home</a>
              <a href="#about" className={activePage === 'about' ? 'active' : ''} onClick={(e) => handleNavLinkClick(e, '#about')}>About us</a>
              <a href="#products" className={activePage === 'our-products' ? 'active' : ''} onClick={(e) => handleNavLinkClick(e, '#products')}>Our Products</a>
              <a href="#shop" className={activePage === 'shop' ? 'active' : ''} onClick={(e) => handleNavLinkClick(e, '#shop')}>Shop</a>
              <a href="#knowledge" className={activePage === 'knowledge' ? 'active' : ''} onClick={(e) => handleNavLinkClick(e, '#knowledge')}>Knowledge Hub</a>
              <a href="#contact" className={activePage === 'contact' ? 'active' : ''} onClick={(e) => handleNavLinkClick(e, '#contact')}>Contact</a>
            </div>

            {/* Search Bar (hidden on mobile, drawer search instead) */}
            <form className="search-bar-wrap" onSubmit={handleSearchSubmit}>
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSuggestionsVisible(true);
                }}
                onFocus={() => setSuggestionsVisible(true)}
              />
              <button type="submit" className="search-btn">
                <SVGIcon name="search" size={20} stroke="#ffffff" />
              </button>

              {/* Desktop Suggestions Dropdown */}
              {suggestionsVisible && searchQuery.trim().length > 0 && renderSuggestionsDropdown(false)}
            </form>

            {/* Top Actions - Disabled/Removed Cart/Wishlist/Login */}
            <div className="top-actions">
              {/* Actions removed for catalog-only website */}
            </div>
          </div>
        </header>

        {/* Mobile Drawer Backdrop */}
        {mobileMenuOpen && (
          <div className="drawer-backdrop" onClick={() => setMobileMenuOpen(false)}></div>
        )}

        {/* Navigation Menu (Drawer on Mobile, Bar on Desktop) */}
        <nav className={`navigation-bar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="container nav-content">
            {/* Drawer Header (Mobile only) */}
            <div className="drawer-header-mobile">
              <div className="drawer-logo-wrap" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img src="/logo/Orbifert Logo.png" alt="ORBIFERT Logo" className="drawer-logo" style={{ maxHeight: '42px' }} />
                <div className="logo-brand-text" style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
                  <span className="brand-name-top" style={{ color: '#363b6d', fontSize: '16px' }}>ORBIFERT</span>
                  <span className="brand-name-sub" style={{ color: '#aaaaaa', fontSize: '10px' }}>ORGANIC PVT. LTD</span>
                  <span className="brand-name-tagline" style={{ fontSize: '9.5px', color: '#f1921d', whiteSpace: 'nowrap', marginTop: '2px' }}>
                    Reviving Soil, <span className="tagline-green" style={{ color: '#7eda6a' }}>Rewarding Farmers</span>
                  </span>
                </div>
              </div>
              <button className="drawer-close-btn" onClick={() => setMobileMenuOpen(false)} aria-label="Close Menu">
                <SVGIcon name="close" size={24} stroke="#ffffff" />
              </button>
            </div>

            {/* Mobile Search Bar inside Drawer */}
            <form className="drawer-search-mobile" onSubmit={handleSearchSubmit}>
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSuggestionsVisible(true);
                }}
                onFocus={() => setSuggestionsVisible(true)}
              />
              <button type="submit">
                <SVGIcon name="search" size={18} stroke="#ffffff" />
              </button>

              {/* Mobile Suggestions Dropdown */}
              {suggestionsVisible && searchQuery.trim().length > 0 && renderSuggestionsDropdown(true)}
            </form>

            {/* Navigation Links */}
            <div className="nav-links">
              <a href="#home" className={activePage === 'home' ? 'active' : ''} onClick={(e) => handleNavLinkClick(e, '#home')}>Home</a>
              <a href="#about" className={activePage === 'about' ? 'active' : ''} onClick={(e) => handleNavLinkClick(e, '#about')}>About us</a>
              <a href="#products" className={activePage === 'our-products' ? 'active' : ''} onClick={(e) => handleNavLinkClick(e, '#products')}>Our Products</a>
              <a href="#shop" className={activePage === 'shop' ? 'active' : ''} onClick={(e) => handleNavLinkClick(e, '#shop')}>
                Shop
              </a>
              <a href="#knowledge" className={activePage === 'knowledge' ? 'active' : ''} onClick={(e) => handleNavLinkClick(e, '#knowledge')}>Knowledge Hub</a>
              <a href="#contact" className={activePage === 'contact' ? 'active' : ''} onClick={(e) => handleNavLinkClick(e, '#contact')}>Contact</a>
            </div>

            {/* Mobile login drawer section removed for catalog-only website */}

            {/* Contact Details in Drawer (Mobile only) */}
            <div className="drawer-contact-mobile">
              <div className="contact-item-drawer">
                <SVGIcon name="phone" size={16} />
                <span>+91 7228064418</span>
              </div>
              <div className="contact-item-drawer">
                <SVGIcon name="mail" size={16} />
                <span>orbifertorganic@gmail.com</span>
              </div>
            </div>

            {/* Currency in Drawer removed */}

            {/* Desktop Contact */}
            <div className="nav-contact-desktop">
              <SVGIcon name="phone" size={18} />
              <span>+91 7228064418</span>
            </div>
          </div>
        </nav>

        {/* Reusable Bulk Order Modal */}
        <div className={`bulk-modal-overlay ${bulkModalOpen ? 'open' : ''}`} onClick={() => setBulkModalOpen(false)}>
          <div className="bulk-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="bulk-modal-close" onClick={() => setBulkModalOpen(false)}>
              <SVGIcon name="close" size={20} />
            </button>

            <h2>Bulk Purchase Enquiry</h2>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px', marginTop: '-10px' }}>
              Looking to purchase Bio-Orbi products in commercial scale or custom quantities? Fill out the fields below, and our dispatch division will send a tailored commercial quotation directly to your inbox.
            </p>

            <form className="bulk-modal-form" onSubmit={handleBulkSubmit}>
              <div className="bulk-modal-form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="Enter your full name"
                  value={bulkFormData.name}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, name: e.target.value })}
                />
              </div>

              <div className="bulk-modal-form-group-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="bulk-modal-form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    required
                    placeholder="name@example.com"
                    value={bulkFormData.email}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, email: e.target.value })}
                  />
                </div>
                <div className="bulk-modal-form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    required
                    placeholder="e.g. +91 9876543210"
                    value={bulkFormData.phone}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="bulk-modal-form-group-row" style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '12px' }}>
                <div className="bulk-modal-form-group">
                  <label>Company / Farm Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Green Valley Agro"
                    value={bulkFormData.company}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, company: e.target.value })}
                  />
                </div>
                <div className="bulk-modal-form-group">
                  <label>Location / State *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Gujarat"
                    value={bulkFormData.location}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, location: e.target.value })}
                  />
                </div>
              </div>

              <div className="bulk-modal-form-group-row" style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '12px' }}>
                <div className="bulk-modal-form-group">
                  <label>Product of Interest *</label>
                  <select
                    required
                    value={bulkFormData.product}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, product: e.target.value })}
                  >
                    <option value="BIO-ORBI Super Novel">BIO-ORBI Super Novel</option>
                    <option value="BIO-ORBI Super">BIO-ORBI Super</option>
                    <option value="BIO-ORBI Super-Prime">BIO-ORBI Super-Prime</option>
                    <option value="BIO-ORBI Prime">BIO-ORBI Prime</option>
                    <option value="BIO-ORBI Super-Plus">BIO-ORBI Super-Plus</option>
                    <option value="BIO-ORBI Plus">BIO-ORBI Plus</option>
                    <option value="BIO-ORBI Organic Manure">BIO-ORBI Organic Manure</option>
                    <option value="BIO-ORBI Bio Enriched Organic Manure">BIO-ORBI Bio Enriched Organic Manure</option>
                    <option value="BIO-ORBI Vermicompost">BIO-ORBI Vermicompost</option>
                  </select>
                </div>
                <div className="bulk-modal-form-group">
                  <label>Quantity Required *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. 500 Litres / 10 Tons"
                    value={bulkFormData.quantity}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, quantity: e.target.value })}
                  />
                </div>
              </div>

              <div className="bulk-modal-form-group">
                <label>Special Instructions / Requirements</label>
                <textarea
                  rows="3"
                  placeholder="Share any details regarding packaging, shipping requirements, custom concentrations etc..."
                  value={bulkFormData.message}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, message: e.target.value })}
                ></textarea>
              </div>

              {bulkError && <div className="bulk-alert-danger" style={{ color: '#ef4444', fontSize: '13px', fontWeight: 600, padding: '10px 0' }}>⚠️ {bulkError}</div>}
              {bulkSuccess && <div className="bulk-alert-success" style={{ color: '#16a34a', fontSize: '13px', fontWeight: 600, padding: '10px 0' }}>✓ Enquiry submitted successfully! Our dispatch team will follow up via email shortly.</div>}

              <button type="submit" className="btn-submit-bulk-modal" disabled={bulkLoading}>
                {bulkLoading ? 'Submitting Enquiry...' : 'Submit Commercial Quote Request'}
              </button>
            </form>
          </div>
        </div>

        {/* Scrolled Mega Dropdown Menu removed */}
      </div>
    </div>
  );
}
